/* eslint-disable no-use-before-define */
/* eslint-disable no-param-reassign */
// DEPENDENCIES
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
// COMPONENTS
import FormBlock from '../../../../components/FormBlock';
import InputBlock from '../../../../components/InputBlock';
import Section from '../../../../components/Section';
import ContentBlock from '../../../../components/ContentBlock';
import ModalBlock from '../../../../components/ModalBlock';
import SelectBlock from '../../../../components/SelectBlock';
// SERVICES AND HELPERS
import * as helper from '../../../../helpers/helper';
import * as connectionService from '../../../../services/management/connectionService';
import * as groupService from '../../../../services/management/groupService';

const ConnectionCreateForm = (props) => {
  const {
    showAlert,
    setIsLoading,
    connectionTypeId,
    fetchConnections,
    isConnectionModalVisible,
    setIsConnectionModalVisible,
    connectionType,
    licensor
  } = props;

  const initialConnectionModel = {
    id: 0,
    connectionTypeId,
    groupId: null,
    size: '',
    weight: '',
    qty1: '',
    qty2: '',
    qty3to5: '',
    qty6to10: '',
    qty11to15: '',
    qty16to20: '',
    qty21to30: '',
    qty31to50: '',
    qty51to100: '',
    qty2to4: '',
    qty5to9: '',
    qty10to24: '',
    qty25to100: '',
    qty40Plus: '',
  };
  const [connectionModel, setConnectionModel] = useState(initialConnectionModel);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    setConnectionModel({
      ...connectionModel,
      connectionTypeId
    });
  }, [connectionTypeId]);

  const fetchGroups = () => {
    groupService.getAllGroupOptions().then((res) => {
      setGroups(res);
    }).catch((ex) => {
      showAlert({ text: ex.message, state: 'error' });
    }).finally(() => {
    });
  };

  const formik = useFormik({
    initialValues: connectionModel,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      id: Yup.number(),
      connectionTypeId: Yup.number(),
      groupId: Yup.number(),
      size: Yup.string().required().max(50),
      weight: Yup.string().required().max(50),
      qty1: Yup.number(),
      qty2: Yup.number(),
      qty3to5: Yup.number(),
      qty6to10: Yup.number(),
      qty11to15: Yup.number(),
      qty16to20: Yup.number(),
      qty21to30: Yup.number(),
      qty31to50: Yup.number(),
      qty51to100: Yup.number(),
      qty2to4: Yup.number(),
      qty5to9: Yup.number(),
      qty10to24: Yup.number(),
      qty25to100: Yup.number(),
      qty40Plus: Yup.number(),
    }),
    onSubmit: (values) => {
      setIsLoading(true);
      createConnection(values);
    },
  });

  const createConnection = (model) => {
    connectionService.createConnection(model).then((res) => {
      showAlert({ text: res.message, state: 'success' });
      fetchConnections();
    }).catch((ex) => {
      showAlert({ text: ex.message, state: 'error' });
    }).finally(() => {
      handleConnectionModalClose();
      setIsLoading(false);
    });
  };

  const handleConnectionModalClose = () => {
    setIsConnectionModalVisible(false);
    setTimeout(() => {
      setConnectionModel(initialConnectionModel);
      formik.resetForm();
      setSelectedGroup(null);
    }, 250);
  };

  return (
    <ModalBlock
      hasCloseAction
      centered
      isVisible={isConnectionModalVisible}
      size="lg"
      contentHeader="Add New Connection"
      primaryModalActionText="Create"
      primaryModalActionColor="primary--bg"
      primaryModalActionOnClick={formik.handleSubmit}
      secondaryModalActionText="Cancel"
      secondaryModalActionColor="danger--bg"
      onHide={handleConnectionModalClose}
    >
      <FormBlock onSubmit={formik.handleSubmit}>
        <Section hasNoContainer>
          <ContentBlock cols={3}>
            <InputBlock
              label="Connection Type"
              isRequired
              inputState="disabled"
              value={connectionType}
            />
          </ContentBlock>
          <ContentBlock cols={4}>
            <SelectBlock
              label="Group"
              placeholder="Select a group"
              options={groups}
              isRequired
              value={selectedGroup}
              onChange={(opt) => {
                opt = opt === null ? [] : opt;
                setSelectedGroup(opt);
                formik.setFieldValue('groupId', opt.value);
              }}
            />
          </ContentBlock>

          <ContentBlock cols={2}>
            <InputBlock
              label="Size"
              isRequired
              errorMessage={formik.errors.size}
              inputState={`${helper.getInputClasses(formik, 'size')}`}
              {...formik.getFieldProps('size')}
            />
          </ContentBlock>

          <ContentBlock cols={3}>
            <InputBlock
              label="Weight"
              isRequired
              errorMessage={formik.errors.weight}
              inputState={`${helper.getInputClasses(formik, 'weight')}`}
              {...formik.getFieldProps('weight')}
            />
          </ContentBlock>

          <ContentBlock cols={3}>
            <InputBlock
              label="QTY 1"
              isRequired
              errorMessage={formik.errors.qty1}
              inputState={`${helper.getInputClasses(formik, 'qty1')}`}
              type="number"
              {...formik.getFieldProps('qty1')}
            />
          </ContentBlock>
          {
            licensor.isDifferent ? (
              <>
                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 2-4"
                    isRequired
                    errorMessage={formik.errors.qty2to4}
                    inputState={`${helper.getInputClasses(formik, 'qty2to4')}`}
                    type="number"
                    {...formik.getFieldProps('qty2to4')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 5-9"
                    isRequired
                    errorMessage={formik.errors.qty5to9}
                    inputState={`${helper.getInputClasses(formik, 'qty5to9')}`}
                    type="number"
                    {...formik.getFieldProps('qty5to9')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 10-24"
                    isRequired
                    errorMessage={formik.errors.qty10to24}
                    inputState={`${helper.getInputClasses(formik, 'qty10to24')}`}
                    type="number"
                    {...formik.getFieldProps('qty10to24')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 25-39"
                    isRequired
                    errorMessage={formik.errors.qty25to100}
                    inputState={`${helper.getInputClasses(formik, 'qty25to100')}`}
                    type="number"
                    {...formik.getFieldProps('qty25to100')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 40+"
                    isRequired
                    errorMessage={formik.errors.qty40Plus}
                    inputState={`${helper.getInputClasses(formik, 'qty40Plus')}`}
                    type="number"
                    {...formik.getFieldProps('qty40Plus')}
                  />
                </ContentBlock>
              </>
            ) : (
              <>
                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 2"
                    isRequired
                    errorMessage={formik.errors.qty2}
                    inputState={`${helper.getInputClasses(formik, 'qty2')}`}
                    type="number"
                    {...formik.getFieldProps('qty2')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 3-5"
                    isRequired
                    errorMessage={formik.errors.qty3to5}
                    inputState={`${helper.getInputClasses(formik, 'qty3to5')}`}
                    type="number"
                    {...formik.getFieldProps('qty3to5')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 6-10"
                    isRequired
                    errorMessage={formik.errors.qty6to10}
                    inputState={`${helper.getInputClasses(formik, 'qty6to10')}`}
                    type="number"
                    {...formik.getFieldProps('qty6to10')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 11-15"
                    isRequired
                    errorMessage={formik.errors.qty11to15}
                    inputState={`${helper.getInputClasses(formik, 'qty11to15')}`}
                    type="number"
                    {...formik.getFieldProps('qty11to15')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 16-20"
                    isRequired
                    errorMessage={formik.errors.qty16to20}
                    inputState={`${helper.getInputClasses(formik, 'qty16to20')}`}
                    type="number"
                    {...formik.getFieldProps('qty16to20')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 21-30"
                    isRequired
                    errorMessage={formik.errors.qty21to30}
                    inputState={`${helper.getInputClasses(formik, 'qty21to30')}`}
                    type="number"
                    {...formik.getFieldProps('qty21to30')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 31-50"
                    isRequired
                    errorMessage={formik.errors.qty31to50}
                    inputState={`${helper.getInputClasses(formik, 'qty31to50')}`}
                    type="number"
                    {...formik.getFieldProps('qty31to50')}
                  />
                </ContentBlock>

                <ContentBlock cols={3}>
                  <InputBlock
                    label="QTY 51-100"
                    isRequired
                    errorMessage={formik.errors.qty51to100}
                    inputState={`${helper.getInputClasses(formik, 'qty51to100')}`}
                    type="number"
                    {...formik.getFieldProps('qty51to100')}
                  />
                </ContentBlock>
              </>
            )
          }
        </Section>
      </FormBlock>
    </ModalBlock>
  );
};

export default ConnectionCreateForm;